import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";

import { IconName } from "../icons/Icon";
import { FileInput } from "./FileInput";
import ImageContainer from "../ImageContainer";
import { Modal } from "../Modal";
import { Button } from "../buttons/Button";

interface ImageInputProps {
    currentFile: File | string | null | undefined;
    onChange: (file: File | null) => void;
    size?: number;
    alt?: string;
    placeholderIconName?: IconName;
    className?: string;
    containerClassName?: string;
    allowedExtensions?: string[];
}

export const ImageInput = ({
    currentFile,
    onChange,
    size = 72,
    alt,
    placeholderIconName,
    className,
    containerClassName,
    allowedExtensions = ["jpg", "jpeg", "png", "gif"],
}: ImageInputProps) => {
    const CROPPER_SIZE = 300;
    const CROPPER_PADDING = 25;

    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [zoom, setZoom] = useState(1);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
        x: number;
        y: number;
        width: number;
        height: number;
    } | null>(null);

    const getCroppedImg = useCallback(
        (
            image: File,
            cropArea: { x: number; y: number; width: number; height: number }
        ): Promise<File> => {
            return new Promise((resolve, reject) => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    reject(new Error("Failed to get canvas context"));
                    return;
                }
                const img = new Image();
                const imageUrl = URL.createObjectURL(image);
                img.src = imageUrl;
                img.onload = () => {
                    try {
                        canvas.width = cropArea.width;
                        canvas.height = cropArea.height;
                        ctx.drawImage(
                            img,
                            cropArea.x,
                            cropArea.y,
                            cropArea.width,
                            cropArea.height,
                            0,
                            0,
                            cropArea.width,
                            cropArea.height
                        );
                        canvas.toBlob(
                            (blob) => {
                                URL.revokeObjectURL(imageUrl);
                                if (blob) {
                                    const file = new File([blob], image.name, {
                                        type: image.type || "image/png",
                                    });
                                    resolve(file);
                                } else {
                                    reject(new Error("Failed to create blob"));
                                }
                            },
                            image.type || "image/png",
                            0.95
                        );
                    } catch (error) {
                        URL.revokeObjectURL(imageUrl);
                        reject(error);
                    }
                };
                img.onerror = () => {
                    URL.revokeObjectURL(imageUrl);
                    reject(new Error("Failed to load image"));
                };
            });
        },
        []
    );

    const handleImageUpload = useCallback(
        (file: File) => {
            onChange(file);
            setUploadedFile(null);
            setCroppedAreaPixels(null);
        },
        [onChange]
    );

    const handleCropComplete = useCallback(async () => {
        if (!uploadedFile || !croppedAreaPixels) {
            return;
        }
        try {
            const croppedImg = await getCroppedImg(
                uploadedFile,
                croppedAreaPixels
            );
            handleImageUpload(croppedImg);
        } catch (error) {
            console.error("Error cropping image:", error);
        }
    }, [getCroppedImg, croppedAreaPixels, uploadedFile, handleImageUpload]);

    return (
        <>
            <div className={`image-input ${className}`}>
                <ImageContainer
                    image={currentFile || null}
                    size={size}
                    alt={
                        alt ||
                        (currentFile instanceof File
                            ? currentFile.name
                            : "תמונה")
                    }
                    containerClassName={containerClassName}
                    placeholderIconName={placeholderIconName}
                />
                <FileInput
                    allowedExtensions={allowedExtensions}
                    currentFile={currentFile}
                    onChange={setUploadedFile}
                    label={"תמונה"}
                />
            </div>

            {uploadedFile && (
                <Modal
                    onClose={() => handleImageUpload(uploadedFile)}
                    style={{
                        width: `${CROPPER_SIZE + CROPPER_PADDING * 2}px`,
                        height: `${CROPPER_SIZE + CROPPER_PADDING * 2 + 80}px`,
                    }}
                >
                    <div className="flex flex-col gap-4 items-center justify-center">
                        <Cropper
                            image={URL.createObjectURL(uploadedFile)}
                            aspect={1 / 1}
                            crop={crop}
                            zoom={zoom}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={(_, croppedAreaPixels) => {
                                setCroppedAreaPixels(croppedAreaPixels);
                            }}
                            style={{
                                containerStyle: {
                                    maxWidth: `${CROPPER_SIZE}px`,
                                    maxHeight: `${CROPPER_SIZE}px`,
                                    position: "absolute",
                                    top: `${CROPPER_PADDING + 10}px`,
                                    right: `${CROPPER_PADDING}px`,
                                },
                                cropAreaStyle: {
                                    maxWidth: `${CROPPER_SIZE * 0.95}px`,
                                    maxHeight: `${CROPPER_SIZE * 0.95}px`,
                                },
                            }}
                        />

                        {croppedAreaPixels && (
                            <div className="absolute bottom-5 left-5 right-5">
                                <Button
                                    variant="primary"
                                    onClick={handleCropComplete}
                                >
                                    שמור תמונה
                                </Button>
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </>
    );
};
